import React, { useEffect, useRef, useState } from 'react';
import Alert from '../../../common/components/Alert';
import axios from '../../../common/utils/interceptor';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import clsx from 'clsx';
import ReactHtmlParser, { DOMNode, Element, domToReact } from 'html-react-parser';
import { NavigateBefore } from '@mui/icons-material';
import Pagination from '@mui/lab/Pagination';
import { apiUrls, routeMappings } from '../../../constants';
import { Rater } from '../../interfaces/Rater';
import { RaterPageContainer } from './RaterPageContainer';
import { RaterSearchResults } from '../../interfaces/RaterSearchResults';
import { RaterStyles } from '../../styles/rater';
import { RatingQuestionStyles } from '../../styles/ratingQuestion';
import { updateRater } from '../../utils/rater';
import { UpdateRaterMode } from '../../constants/updateRaterMode';
import { useNavigate, useLocation, useParams } from 'react-router';
import { useRaterNominationDetails } from '../../store/mfsStoreApi';
import { useTranslation } from 'react-i18next';
import { GARouteMappings, GAPageViewMappings } from '../../../constants/gaMappings';
import { sendPageViewDetails } from '../../../common/utils/gaEvents';
import { SearchRaterCard } from './SearchRaterCard';
import { getServerDomain } from '../../utils/helper';
import { getCookie } from '../../../common/utils/cookies';
import { WCAGStrandardStyles } from '../../../common/styles/WCAGStandardStyles';

export const SearchRaterResultsPage = (): React.JSX.Element => {
  /** Page View GA method */
  useEffect(() => {
    sendPageViewDetails(GARouteMappings.SHL360_SEARCH, GAPageViewMappings.SHL360_SEARCH);
  }, []);

  const { classes } = RaterStyles();
  const { classes: clsRQ } = RatingQuestionStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const routeParams = useParams<{ roleId?: string }>();
  const errAlertRef = useRef<HTMLDivElement>(null);
  const pageHeadingRef = useRef<HTMLHeadingElement>(null);
  const path = location.pathname.split('/');
  const { classes: WCAGClasses } = WCAGStrandardStyles();
  const { ratingId, setNewAddedRaterId, details } = useRaterNominationDetails();
  const { disableAddNewRater } = details.page;

  const [loading, setLoading] = useState<boolean>(false);
  const [errorKey, setErrorKey] = useState<string>('');
  const [resultsLimit] = useState<number>(10);
  const [searchResults, setSearchResults] = useState<RaterSearchResults>({
    totalSearchRow: 0,
    currentPageNo: 1,
    pageSize: 0,
    results: [],
  });

  const fetchRaterSearchResults = async (pageNumber: number): Promise<void> => {
    try {
      setLoading(true);
      setErrorKey('');
      const request = {
        firstName: location.state && location.state.firstName,
        lastName: location.state && location.state.lastName,
        email: location.state && location.state.email,
        personalNumber: location.state && location.state.personalNumber,
        country: location.state && location.state.country,
        region: location.state && location.state.region,
        function: location.state && location.state.function,
        level: location.state && location.state.level,
        pageNumber,
        pageSize: resultsLimit,
      };
      const config = { headers: { sid: getCookie('sid') }, withCredentials: false };
      const { data } = await axios.post(getServerDomain(apiUrls.POST_MFS_RATER_SEARCH), request, config);
      // const { data } = await axios.get(apiUrls.POST_MFS_RATER_SEARCH);

      if (!data.totalSearchRow && data.totalSearchRow === 0) {
        setErrorKey('noResultsMessage');
      }

      setSearchResults(data);
    } catch (e) {
      setErrorKey('internalServerError');
    } finally {
      setLoading(false);

      if (pageHeadingRef.current) {
        pageHeadingRef.current.focus();
      }
    }
  };

  function scrollToError(errKey: string, errorAlertRef: React.RefObject<HTMLDivElement>): void {
    if (errKey && errorAlertRef.current) {
      errorAlertRef.current.scrollIntoView();
    }
  }

  useEffect(() => {
    // check if page is reloaded
    if (location.state === undefined) {
      navigate(`${routeMappings.MFS}${routeMappings.SEARCH_RATER}/${path[path.length - 1]}`);
    }

    fetchRaterSearchResults(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollToError(errorKey, errAlertRef);
  }, [errorKey, loading]);

  const addRater = async (rater: Rater): Promise<void> => {
    setLoading(true);
    const responseErrorKey = await updateRater(UpdateRaterMode.SEARCH_ADD, rater, routeParams.roleId);
    setErrorKey(responseErrorKey);
    setLoading(false);

    // Navigate back to rater nomination landing page on success
    if (responseErrorKey === '') {
      setNewAddedRaterId(rater.raterId.toString());
      navigate(`${routeMappings.MFS}${routeMappings.RATERS}/${ratingId}`);
    }
  };

  const onAddClick = (rater: Rater) => (): void => {
    addRater(rater);
    scrollToError(errorKey, errAlertRef);
  };

  const redirectToAddRater = (): void => {
    navigate(`${routeMappings.MFS}${routeMappings.ADD_NEW_RATER}/${routeParams.roleId}`);
  };

  const onBackClick = (): void => {
    navigate(`${routeMappings.MFS}${routeMappings.SEARCH_RATER}/${routeParams.roleId}`);
  };

  const onPageChange = (event: React.ChangeEvent<unknown>, page: number): void => {
    fetchRaterSearchResults(page);
  };

  if (loading) {
    return (
      <RaterPageContainer>
        <Box mt={20} mb={6}>
          <CircularProgress color="primary" />
        </Box>
      </RaterPageContainer>
    );
  }

  return (
    <RaterPageContainer>
      <Grid xs={12} item>
        <Box mb={1} mt={6} textAlign="center">
          <Typography ref={pageHeadingRef} tabIndex={-1} className={`${classes.pageTitle} ${WCAGClasses.h2}`}>
            {searchResults.totalSearchRow > 0
              ? `${t('searchResults')} (${searchResults.totalSearchRow})`
              : t('noResults')}
          </Typography>
        </Box>
      </Grid>

      <Grid xs={12} item>
        <Box mt={3}>
          {searchResults.results &&
            searchResults.results.map((rater) => (
              <SearchRaterCard key={rater.raterId} rater={rater} onAddClick={onAddClick} />
            ))}
        </Box>
      </Grid>

      {/** alert message in case api fails */}
      <div ref={errAlertRef} className={classes.alertBox}>
        <Alert
          show={errorKey !== ''}
          state="error"
          gridClassName={errorKey !== 'noResultsMessage' ? classes.paddingTopLeftRight : ''}>
          {errorKey === 'noResultsMessage' ? (
            <>
              <Box mb={2}>
                <Typography variant="body1">{t(errorKey)}</Typography>
              </Box>

              {!disableAddNewRater &&
                ReactHtmlParser(t('noResultsMessage2'), {
                  replace: (node: DOMNode): React.JSX.Element | undefined => {
                    const domNode = node as Element;
                    const domNodeChildren = domNode.children as DOMNode[];
                    if (node.type === 'tag' && node.name === 'a') {
                      return (
                        <span
                          className={classes.noSearchResultsLink}
                          aria-label={domNodeChildren && domNodeChildren[0].toString()}
                          onClick={(): void => {
                            redirectToAddRater();
                          }}>
                          {domNodeChildren && domToReact(domNodeChildren)}
                        </span>
                      );
                    }
                  },
                })}
            </>
          ) : (
            t(errorKey)
          )}
        </Alert>
      </div>

      {!['internalServerError', 'noResultsMessage'].includes(errorKey) && (
        <Grid xs={12} item>
          <Box className={classes.pagination}>
            <Typography variant="body1">
              {t('resultsPaginationText', {
                x:
                  searchResults.currentPageNo - 1 === 0
                    ? 1
                    : (searchResults.currentPageNo - 1) * searchResults.pageSize + 1,
                y:
                  searchResults.results.length < searchResults.pageSize
                    ? (searchResults.currentPageNo - 1) * searchResults.pageSize + searchResults.results.length
                    : (searchResults.currentPageNo - 1) * searchResults.pageSize + searchResults.pageSize,
                total: searchResults.totalSearchRow,
              })}
            </Typography>

            <Pagination
              count={Math.ceil(searchResults.totalSearchRow / searchResults.pageSize)}
              page={searchResults.currentPageNo}
              size="small"
              onChange={onPageChange}
            />
          </Box>
        </Grid>
      )}

      <Grid xs={12} item>
        <Box display="flex" mb={4} mt={4}>
          <Grid className={classes.textAlignLeft}>
            <Button
              variant="contained"
              className={clsx(classes.doneButton, classes.width100)}
              onClick={onBackClick}
              aria-label={t('back')}>
              <NavigateBefore className={clsRQ.backArrowIcon} /> {t('back')}
            </Button>
          </Grid>
        </Box>
      </Grid>
    </RaterPageContainer>
  );
};
